import moment from "moment-timezone";
import { Text } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import {
  Accordion,
  AccordionArrow,
  AccordionHeadingContainer,
  Header,
  PageWrapper,
} from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { APP_ENV } from "src/shared/utils/env";
import globalConstants from "src/theme/globalConstants";

import MockTimeControl from "../../check-in/components/MockTimeControl";
import { useMockableNow } from "../../check-in/hooks/useMockableNow";
import VolunteerShiftModal from "../../shared/components/VolunteerShiftModal";
import { useGetVolunteerShiftAvailabilityQuery } from "../../shift-schedule/graphql/getVolunteerShiftAvailability.generated";
import { useGetVolunteerShiftScheduleQuery } from "../../shift-schedule/graphql/getVolunteerShiftSchedule.generated";
import { PRIMARY_SHIFTS, SHIFT_CATEGORIES } from "../constants";

import LiveAttendancePanel from "./LiveAttendancePanel";
import ShiftCard from "./ShiftCard";

// const POLL_MS = 20_000; // Paused to reduce HAPI load; reload for updates.

const AdminDashboard: React.FC = () => {
  const [selectedShift, setSelectedShift] = useState<number | null>(null);
  const [viewModal, setViewModal] = useState(false);
  const [lastRefresh, setLastRefresh] = useState("");
  const { now, mockNow, setMockNow } = useMockableNow();

  const { data: volunteerShiftsData, refetch: refetchVolunteerShifts } =
    useGetVolunteerShiftScheduleQuery({
      variables: { event_id: EVENT_ID },
      // pollInterval: POLL_MS,
      fetchPolicy: "cache-and-network",
    });
  const { data: shiftAvailabilityData, refetch: refetchShiftAvailability } =
    useGetVolunteerShiftAvailabilityQuery({
      variables: { event_id: EVENT_ID },
      // pollInterval: POLL_MS,
      fetchPolicy: "cache-and-network",
    });

  const refetchAllShifts = useCallback(() => {
    void refetchVolunteerShifts();
    void refetchShiftAvailability();
  }, [refetchVolunteerShifts, refetchShiftAvailability]);

  const handleShiftClick = (shift: { shift_id: number }) => {
    setSelectedShift(shift.shift_id);
    setViewModal(true);
  };

  const statuses: ("Upcoming" | "Ongoing" | "Completed")[] = [
    "Ongoing",
    "Upcoming",
    "Completed",
  ];

  const groupShifts = useCallback(() => {
    const upcomingRange = now.clone().add(24, "hours");
    const fourHoursAgo = now.clone().subtract(4, "hours");
    const startOfToday = now.clone().startOf("day");
    const previousRange = now.hour() >= 4 ? startOfToday : fourHoursAgo;
    const shifts = volunteerShiftsData?.volunteerShiftSchedule ?? [];

    const grouped: Record<
      string,
      {
        Upcoming: typeof shifts;
        Ongoing: typeof shifts;
        Completed: typeof shifts;
      }
    > = {};

    SHIFT_CATEGORIES.forEach((category) => {
      grouped[category] = { Upcoming: [], Ongoing: [], Completed: [] };
    });

    shifts.forEach((shift) => {
      const start = moment(shift.start_time);
      const end = moment(shift.end_time);
      const category = PRIMARY_SHIFTS.includes(shift.category_name)
        ? shift.category_name
        : "Supplementary";

      if (!grouped[category]) {
        grouped[category] = { Upcoming: [], Ongoing: [], Completed: [] };
      }

      if (start.isBefore(now) && end.isAfter(now)) {
        grouped[category].Ongoing.push(shift);
      } else if (start.isAfter(now) && start.isBefore(upcomingRange)) {
        grouped[category].Upcoming.push(shift);
      } else if (end.isBefore(now) && end.isAfter(previousRange)) {
        grouped[category].Completed.push(shift);
      }
    });

    return grouped;
  }, [volunteerShiftsData, now]);

  const groupedShifts = groupShifts();
  const allShifts = volunteerShiftsData?.volunteerShiftSchedule ?? [];
  const allAvailability = useMemo(
    () => shiftAvailabilityData?.volunteerShiftAvailability ?? [],
    [shiftAvailabilityData]
  );

  const availabilityByShift = useMemo(
    () =>
      new Map(
        allAvailability.map((availability) => [
          availability.shift_id,
          availability,
        ])
      ),
    [allAvailability]
  );

  const getVolunteerStats = useCallback(
    (category: string) => {
      const categoryShifts = groupedShifts[category];
      if (!categoryShifts) {
        return {
          totalVolunteers: 0,
          checkedInVolunteers: 0,
          missingVolunteers: 0,
        };
      }

      const allCategoryShifts = [
        ...categoryShifts.Upcoming,
        ...categoryShifts.Ongoing,
        ...categoryShifts.Completed,
      ];

      const totalVolunteers = allCategoryShifts.reduce(
        (total, shift) =>
          total + (availabilityByShift.get(shift.shift_id)?.signup_count ?? 0),
        0
      );
      const checkedInVolunteers = categoryShifts.Ongoing.reduce(
        (total, shift) =>
          total +
          (availabilityByShift.get(shift.shift_id)?.checked_in_count ?? 0),
        0
      );
      const ongoingVolunteers = categoryShifts.Ongoing.reduce(
        (total, shift) =>
          total + (availabilityByShift.get(shift.shift_id)?.signup_count ?? 0),
        0
      );
      const missingVolunteers = ongoingVolunteers - checkedInVolunteers;

      return { totalVolunteers, checkedInVolunteers, missingVolunteers };
    },
    [groupedShifts, availabilityByShift]
  );

  useEffect(() => {
    if (viewModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewModal]);

  useEffect(() => {
    setLastRefresh(
      moment().tz("America/New_York").format("ddd, MMM D [at] h:mm A")
    );
  }, [volunteerShiftsData, shiftAvailabilityData]);

  return (
    <>
      {selectedShift && (
        <VolunteerShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
          onDeleteShift={() => {
            setViewModal(false);
            void refetchAllShifts();
          }}
          onChange={() => {
            void refetchAllShifts();
          }}
        />
      )}
      <Header
        title="Volunteer Dashboard"
        subtitle="Scan a volunteer, view anything related to volunteering!"
      />
      <PageWrapper fullWidth>
        <PageHeadingWrapper>
          <HeaderText>Today&apos;s Shifts</HeaderText>
          <Text
            type="subtitle"
            style={{
              color: globalConstants?.color?.brandSecondary as string,
            }}
          >
            {`Last Refresh - ${lastRefresh}`}
          </Text>
          <div className="text-text-tertiary mb-4">
            All times are in Eastern Time (EDT)
          </div>
        </PageHeadingWrapper>

        {(APP_ENV === "development" || APP_ENV === "staging") && (
          <MockTimeControl
            now={now}
            mockNow={mockNow}
            setMockNow={setMockNow}
          />
        )}

        <LiveAttendancePanel
          shifts={allShifts}
          availability={allAvailability}
          now={now}
          onShiftClick={handleShiftClick}
        />

        {SHIFT_CATEGORIES.map((category) => {
          const { totalVolunteers, checkedInVolunteers, missingVolunteers } =
            getVolunteerStats(category);
          return (
            <Accordion
              key={category}
              heading={
                <ShiftContainer>
                  <ShiftInfo>
                    <ShiftName>{category}</ShiftName>
                    <ShiftDetails>
                      {Object.values(groupedShifts[category]).reduce(
                        (acc, arr) => acc + arr.length,
                        0
                      )}{" "}
                      shifts • {totalVolunteers} volunteers •{" "}
                      {checkedInVolunteers} checked in
                      {missingVolunteers > 0
                        ? ` • ${missingVolunteers} missing`
                        : ""}
                    </ShiftDetails>
                  </ShiftInfo>
                  <AccordionArrow />
                </ShiftContainer>
              }
            >
              <StatusContainer>
                {statuses.map((status) => {
                  const shifts = groupedShifts[category][status];
                  if (!shifts || shifts.length === 0) return null;

                  return (
                    <div key={status}>
                      <Text
                        type="h3"
                        style={{
                          color: globalConstants?.color
                            ?.brandSecondary as string,
                        }}
                      >
                        {`${status} Shifts`}
                      </Text>
                      <CardsContainer>
                        {shifts.map((shift) => {
                          const availability = availabilityByShift.get(
                            shift.shift_id
                          );
                          const signupCount = availability?.signup_count ?? 0;
                          const checkedInCount =
                            availability?.checked_in_count ?? 0;
                          const missingCount = signupCount - checkedInCount;

                          return (
                            <ShiftCard
                              key={shift.shift_id}
                              shiftName={shift.name}
                              startTime={shift.start_time}
                              endTime={shift.end_time}
                              shiftLocation={shift.location || "N/A"}
                              registeredUsers={signupCount}
                              maxUsers={shift.max_volunteer_count ?? 0}
                              onClick={() => handleShiftClick(shift)}
                              checkedIn={false}
                              now={now}
                              attendance={
                                status === "Ongoing"
                                  ? {
                                      checkedIn: checkedInCount,
                                      missing: missingCount,
                                    }
                                  : undefined
                              }
                            />
                          );
                        })}
                      </CardsContainer>
                    </div>
                  );
                })}
              </StatusContainer>
            </Accordion>
          );
        })}
      </PageWrapper>
    </>
  );
};

const HeaderText = styled.h1`
  color: #000;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 34px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  margin-bottom: 8px;
`;

const ShiftContainer = styled(AccordionHeadingContainer)`
  display: flex;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #d1d5db;
  border-bottom: 1px solid #d1d5db;
`;

const ShiftInfo = styled.div`
  flex-grow: 1;
`;

const ShiftName = styled.h2`
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const ShiftDetails = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  font-size: 14px;
  margin: 4px 0 0;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding: 16px 0 32px;

  ${({ theme }) => theme.mediaQueries.medium`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    grid-template-columns: 1fr;
  `}
`;

const StatusContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  padding: 24px 16px;
`;

const PageHeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
`;

export default AdminDashboard;
